import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify/';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectAuth, signOut } from 'app/store/authSlice';
import { selectFeatureFlags } from 'app/store/featureFlagsSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { isMobileAppShell } from 'app/auth/nativePasskeyBridge';
import SessionTimeoutDialog from 'app/auth/SessionTimeoutDialog';
import {
  ACTIVITY_WRITE_THROTTLE_MS,
  DEFAULT_TIMEOUT_MINUTES,
  IDLE_LOGOUT_STORAGE_KEY,
  SESSION_TIMEOUT_CHECK_INTERVAL_MS,
  SESSION_TIMEOUT_WARNING_SECONDS,
  markIdleLogout,
  readEffectiveTimeoutMinutes,
  readLastActivityAt,
  resolveEffectiveTimeoutMinutes,
  resolveSessionTimeoutConfig,
  shouldEnforceBootIdle,
  writeEffectiveTimeoutMinutes,
  writeLastActivityAt,
} from 'app/auth/sessionTimeout';

const ACTIVITY_EVENTS: Array<keyof WindowEventMap> = ['mousedown', 'keydown', 'scroll', 'touchstart'];

/**
 * For SSO sessions, Amplify v4's Auth.signOut() sees the hosted-UI marker and
 * performs a FULL-PAGE redirect to Cognito /logout mid-thunk — which (a)
 * yanked the /logged-out screen away before it could be read, and (b) unloaded
 * the page before the thunk's cleanup ran, leaving a stale activity stamp that
 * locked SSO users into a login→logout loop. Idle logouts remove the marker
 * first so signOut tears the session down locally, exactly like a password
 * session. Trade-off (accepted, matches existing behavior): the Cognito
 * hosted-UI cookie (~1h) isn't cleared on IDLE logout — but even the normal
 * logout's /logout redirect never ended the Entra session, so same-device
 * silent re-login has always been possible for SSO; local tokens ARE fully
 * cleared either way. User-initiated logout keeps the full redirect.
 */
const suppressHostedUiSignOutRedirect = (): void => {
  try {
    localStorage.removeItem('amplify-signin-with-hostedUI');
  } catch {
    // ignore — worst case Amplify redirects as before
  }
};

/**
 * SEE-219: inactivity session timeout (see sessionTimeout.ts for resolution).
 *
 * Tracks user activity in localStorage — shared across tabs and included in
 * the mobile shell's localStorage backup — and evaluates the idle window
 * every 15s and on tab-visible. The final 10 minutes show the Figma "Session
 * time out" dialog; "Continue session" resets the full window, activity alone
 * does not dismiss it. Expiry lands on /logged-out (the overnight-tab
 * message screen); other open tabs follow instantly via the `storage` event
 * on the idle-logout marker.
 *
 * Multi-company users are governed by the timeout of their SELECTED company:
 * this reads selectedPaidol, so switching companies swaps the duration.
 *
 * Renders nothing while unauthenticated. Mounted once in App.js.
 */
function SessionTimeoutGuard(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector(selectAuth);
  const { selectedPaidol } = useAppSelector(selectUserCompanies);
  // Optional-chained: partial test stores may not register the flags slice.
  const flags = useAppSelector(selectFeatureFlags)?.flags;

  const isAuthenticated = Boolean(user);

  // Flag key is lowercase in AppConfig; camelCase kept as a fallback in case
  // another environment's flag was created with the old key.
  const flagValue: unknown = flags?.sessiontimeoutconfig ?? flags?.sessionTimeoutConfig;
  const timeoutMinutes = useMemo(() => {
    const config = resolveSessionTimeoutConfig(flagValue, isMobileAppShell() ? 'mobile' : 'web');
    return resolveEffectiveTimeoutMinutes(selectedPaidol?.sessionTimeoutSettings, config);
  }, [flagValue, selectedPaidol?.sessionTimeoutSettings]);

  // Epoch ms at which the idle logout fires; null while no warning is due.
  const [warningDeadline, setWarningDeadline] = useState<number | null>(null);
  const warningOpenRef = useRef(false);
  warningOpenRef.current = warningDeadline !== null;
  const loggingOutRef = useRef(false);
  const bootCheckedRef = useRef(false);
  // SEE-1456: the periodic checker must not run until the boot check has
  // validated (or reset) the persisted activity stamp. The boot check awaits
  // Auth.currentSession() before deciding, while the periodic effect used to
  // call check() synchronously the moment the user authenticated — so a
  // stamp left over from a naturally-expired previous session (no tab open
  // to clean it up) sent a FRESH login straight to /logged-out before the
  // boot check's auth_time guard could rule the stamp stale. State, not a
  // ref: the periodic effect has to re-run when this flips.
  const [bootCheckDone, setBootCheckDone] = useState(false);

  // Idle expiry: land on the logged-out message screen (outside RequireAuth,
  // so an overnight tab keeps showing it), stamping the cross-tab marker
  // first so every other open tab follows immediately.
  const idleLogout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    setWarningDeadline(null);
    markIdleLogout(Date.now());
    suppressHostedUiSignOutRedirect();
    navigate('/logged-out');
    dispatch(signOut());
  }, [dispatch, navigate]);
  // react-router <6.4 re-creates `navigate` on every route change, so
  // idleLogout is identity-unstable. The boot effect must NOT key on it: a
  // route change while the boot check awaited Auth.currentSession() aborted
  // the in-flight check (cleanup set active=false) while bootCheckedRef
  // blocked a redo — stranding bootCheckDone false and silently disabling
  // idle enforcement for the whole session. The ref keeps the callback fresh
  // without making effects re-run on navigation.
  const idleLogoutRef = useRef(idleLogout);
  idleLogoutRef.current = idleLogout;

  // Another tab already logged out — tear down locally and show the screen.
  const followExternalLogout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    setWarningDeadline(null);
    suppressHostedUiSignOutRedirect();
    navigate('/logged-out');
    dispatch(signOut());
  }, [dispatch, navigate]);

  // The dialog's "Log out": a normal user-initiated sign out, straight to login.
  const userLogout = useCallback(() => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    setWarningDeadline(null);
    dispatch(signOut());
  }, [dispatch]);

  // "Continue session": restart the full configured window.
  const continueSession = useCallback(() => {
    writeLastActivityAt(Date.now());
    setWarningDeadline(null);
  }, []);

  // Cache the resolved timeout once the paidol is loaded so the next boot
  // check (before the paidol loads) uses the org's value, not the default.
  useEffect(() => {
    if (isAuthenticated && selectedPaidol?.id) {
      writeEffectiveTimeoutMinutes(timeoutMinutes);
    }
  }, [isAuthenticated, selectedPaidol?.id, timeoutMinutes]);

  // Boot check: an activity timestamp restored from a previous visit (or from
  // the mobile shell's localStorage backup) that is already past the window
  // means the session must not be resumed — but ONLY if the stamp belongs to
  // this session. It is compared against the ID token's auth_time: a stamp
  // older than the login is leftover from a previous session (an SSO
  // sign-out redirect can unload the page before cleanup runs) and enforcing
  // it locked SSO users into a permanent login→logout loop. Fresh logins are
  // never killed at boot; restored sessions (auth_time predates the stamp)
  // still are.
  useEffect(() => {
    if (!isAuthenticated) {
      loggingOutRef.current = false;
      bootCheckedRef.current = false;
      setBootCheckDone(false);
      return undefined;
    }
    if (bootCheckedRef.current) return undefined;
    bootCheckedRef.current = true;

    let active = true;
    (async () => {
      let authTimeMs: number | null = null;
      try {
        const session = await Auth.currentSession();
        const authTime = (session.getIdToken().payload as { auth_time?: number }).auth_time;
        authTimeMs = typeof authTime === 'number' ? authTime * 1000 : null;
      } catch {
        // Token unreadable — fail open here; the AuthProvider poll owns
        // tearing down genuinely broken sessions.
        authTimeMs = null;
      }
      if (!active || loggingOutRef.current) return;

      const cachedTimeout = readEffectiveTimeoutMinutes() ?? DEFAULT_TIMEOUT_MINUTES;
      const lastActivityAt = readLastActivityAt();
      if (shouldEnforceBootIdle(Date.now(), lastActivityAt, authTimeMs, cachedTimeout)) {
        idleLogoutRef.current();
        return;
      }
      writeLastActivityAt(Date.now());
      setBootCheckDone(true);
    })();
    return () => {
      active = false;
    };
    // Keyed on isAuthenticated ONLY (idleLogout via ref): re-running on
    // navigation is exactly the race that stranded bootCheckDone. See the
    // idleLogoutRef comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Activity tracking + periodic idle evaluation + cross-tab logout signal.
  // Gated on bootCheckDone (SEE-1456): enforcement starts only once the boot
  // check has written a trustworthy activity stamp for this session.
  useEffect(() => {
    if (!isAuthenticated || !bootCheckDone) {
      setWarningDeadline(null);
      return undefined;
    }

    let lastWrite = 0;
    const onActivity = () => {
      // While the warning is showing, only the dialog buttons decide — a
      // stray mouse move must not silently extend the session.
      if (warningOpenRef.current || loggingOutRef.current) return;
      const now = Date.now();
      if (now - lastWrite >= ACTIVITY_WRITE_THROTTLE_MS) {
        lastWrite = now;
        writeLastActivityAt(now);
      }
    };

    const check = () => {
      if (loggingOutRef.current) return;
      const now = Date.now();
      const lastActivityAt = readLastActivityAt();
      if (lastActivityAt === null) {
        // Storage was cleared out from under us — restart the window.
        writeLastActivityAt(now);
        return;
      }
      const deadline = lastActivityAt + timeoutMinutes * 60 * 1000;
      if (now >= deadline) {
        idleLogout();
      } else if (now >= deadline - SESSION_TIMEOUT_WARNING_SECONDS * 1000) {
        setWarningDeadline(deadline);
      } else {
        // Activity in another tab pushed the deadline out — dismiss.
        setWarningDeadline(null);
      }
    };

    // `storage` fires only in OTHER tabs, so this is exactly the "some tab
    // logged out, follow it right now" signal (overnight tabs included —
    // background timers are throttled but still run, so the expiring tab
    // stamps the marker within moments of the deadline).
    const onStorage = (event: StorageEvent) => {
      if (event.key === IDLE_LOGOUT_STORAGE_KEY && event.newValue !== null) {
        followExternalLogout();
      }
    };

    ACTIVITY_EVENTS.forEach((eventName) =>
      window.addEventListener(eventName, onActivity, { capture: true, passive: true })
    );
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('storage', onStorage);
    const interval = setInterval(check, SESSION_TIMEOUT_CHECK_INTERVAL_MS);
    check();

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) =>
        window.removeEventListener(eventName, onActivity, { capture: true } as EventListenerOptions)
      );
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('storage', onStorage);
      clearInterval(interval);
    };
  }, [isAuthenticated, bootCheckDone, timeoutMinutes, idleLogout, followExternalLogout]);

  if (!isAuthenticated || warningDeadline === null) {
    return null;
  }

  return (
    <SessionTimeoutDialog
      open
      deadline={warningDeadline}
      onExpired={idleLogout}
      onLogout={userLogout}
      onContinue={continueSession}
    />
  );
}

export default SessionTimeoutGuard;
